/**
 * Media Service
 * 
 * Handles all media upload-related API calls.
 */

import api from '../utils/api';

/**
 * Upload an image file
 * @param {File} file - Image file to upload
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise} - Upload response with image URL
 */
export const uploadImage = async (file, onProgress = null) => {
  try {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
    if (!allowedTypes.includes(file.type)) {
      return {
        success: false,
        error: 'Unsupported file format. Allowed formats: JPG, JPEG, PNG, GIF, WEBP, BMP',
        imageUrl: null,
      };
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      return {
        success: false,
        error: 'File size exceeds 10 MB limit',
        imageUrl: null,
      };
    }

    // Create FormData
    const formData = new FormData();
    formData.append('file', file);

    // Use uploadFile for progress tracking if callback provided, otherwise use post
    let response;
    if (onProgress) {
      response = await api.uploadFile('/storage/blogs/media/upload-image', formData, onProgress);
    } else {
      response = await api.post('/storage/blogs/media/upload-image', formData);
    }

    if (response.status === 'success' && response.image_url) {
      return {
        success: true,
        imageUrl: response.image_url,
        message: response.message || 'Image uploaded successfully',
      };
    }

    return {
      success: false,
      error: response.message || response.error || 'Failed to upload image',
      imageUrl: null,
    };
  } catch (error) {
    console.error('Upload image error:', error);
    
    let errorMessage = 'Failed to upload image. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      imageUrl: null,
    };
  }
};

/**
 * Upload a video file
 * @param {File} file - Video file to upload
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise} - Upload response with video URL
 */
export const uploadVideo = async (file, onProgress = null) => {
  try {
    // Validate file type
    const allowedTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime', 'video/x-msvideo'];
    const allowedExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi'];
    const fileName = file.name.toLowerCase();
    const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
    
    if (!allowedTypes.includes(file.type) && !hasValidExtension) {
      return {
        success: false,
        error: 'Unsupported file format. Allowed formats: MP4, WEBM, OGG, MOV, AVI',
        videoUrl: null,
      };
    }

    // Validate file size (100MB max)
    const maxSize = 100 * 1024 * 1024; // 100MB in bytes
    if (file.size > maxSize) {
      return {
        success: false,
        error: 'File size exceeds 100 MB limit',
        videoUrl: null,
      };
    }

    // Create FormData
    const formData = new FormData();
    formData.append('file', file);

    // Use uploadFile for progress tracking if callback provided, otherwise use post
    let response;
    if (onProgress) {
      response = await api.uploadFile('/storage/blogs/media/upload-video', formData, onProgress);
    } else {
      response = await api.post('/storage/blogs/media/upload-video', formData);
    }

    if (response.status === 'success' && response.video_url) {
      return {
        success: true,
        videoUrl: response.video_url,
        message: response.message || 'Video uploaded successfully',
      };
    }

    return {
      success: false,
      error: response.message || response.error || 'Failed to upload video',
      videoUrl: null,
    };
  } catch (error) {
    console.error('Upload video error:', error);
    
    let errorMessage = 'Failed to upload video. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      videoUrl: null,
    };
  }
};

/**
 * Upload multiple images in bulk
 * @param {File[]} files - Array of image files to upload (max 20)
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise} - Upload response with image URLs array
 */
export const uploadImagesBulk = async (files, onProgress = null) => {
  try {
    // Validate number of files (max 20)
    if (files.length > 20) {
      return {
        success: false,
        error: 'Maximum 20 images allowed per upload',
        imageUrls: [],
        count: 0,
      };
    }

    // Validate file types and sizes
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
    const maxSize = 10 * 1024 * 1024; // 10MB per image

    const invalidFiles = [];
    files.forEach((file, index) => {
      if (!allowedTypes.includes(file.type)) {
        invalidFiles.push(`${file.name} (index ${index + 1}): Unsupported format`);
      }
      if (file.size > maxSize) {
        invalidFiles.push(`${file.name} (index ${index + 1}): Exceeds 10MB limit`);
      }
    });

    if (invalidFiles.length > 0) {
      return {
        success: false,
        error: `Invalid files:\n${invalidFiles.join('\n')}`,
        imageUrls: [],
        count: 0,
      };
    }

    // Create FormData
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    // Use uploadFile for progress tracking if callback provided, otherwise use post
    let response;
    if (onProgress) {
      response = await api.uploadFile('/storage/blogs/media/upload-images-bulk', formData, onProgress);
    } else {
      response = await api.post('/storage/blogs/media/upload-images-bulk', formData);
    }

    if (response.status === 'success' && response.image_urls) {
      return {
        success: true,
        imageUrls: response.image_urls,
        count: response.count || response.image_urls.length,
        message: response.message || 'Images uploaded successfully',
      };
    }

    return {
      success: false,
      error: response.message || response.error || 'Failed to upload images',
      imageUrls: [],
      count: 0,
    };
  } catch (error) {
    console.error('Upload images bulk error:', error);
    
    let errorMessage = 'Failed to upload images. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      imageUrls: [],
      count: 0,
    };
  }
};

/**
 * Upload multiple videos in bulk
 * @param {File[]} files - Array of video files to upload (max 10)
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise} - Upload response with video URLs array
 */
export const uploadVideosBulk = async (files, onProgress = null) => {
  try {
    // Validate number of files (max 10)
    if (files.length > 10) {
      return {
        success: false,
        error: 'Maximum 10 videos allowed per upload',
        videoUrls: [],
        count: 0,
      };
    }

    // Validate file types and sizes
    const allowedTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime', 'video/x-msvideo'];
    const allowedExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi'];
    const maxSize = 100 * 1024 * 1024; // 100MB per video

    const invalidFiles = [];
    files.forEach((file, index) => {
      const fileName = file.name.toLowerCase();
      const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
      
      if (!allowedTypes.includes(file.type) && !hasValidExtension) {
        invalidFiles.push(`${file.name} (index ${index + 1}): Unsupported format`);
      }
      if (file.size > maxSize) {
        invalidFiles.push(`${file.name} (index ${index + 1}): Exceeds 100MB limit`);
      }
    });

    if (invalidFiles.length > 0) {
      return {
        success: false,
        error: `Invalid files:\n${invalidFiles.join('\n')}`,
        videoUrls: [],
        count: 0,
      };
    }

    // Create FormData
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    // Use uploadFile for progress tracking if callback provided, otherwise use post
    let response;
    if (onProgress) {
      response = await api.uploadFile('/storage/blogs/media/upload-videos-bulk', formData, onProgress);
    } else {
      response = await api.post('/storage/blogs/media/upload-videos-bulk', formData);
    }

    if (response.status === 'success' && response.video_urls) {
      return {
        success: true,
        videoUrls: response.video_urls,
        count: response.count || response.video_urls.length,
        message: response.message || 'Videos uploaded successfully',
      };
    }

    return {
      success: false,
      error: response.message || response.error || 'Failed to upload videos',
      videoUrls: [],
      count: 0,
    };
  } catch (error) {
    console.error('Upload videos bulk error:', error);
    
    let errorMessage = 'Failed to upload videos. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      videoUrls: [],
      count: 0,
    };
  }
};

/**
 * Delete an image file
 * @param {string} filename - Name of the image file to delete
 * @returns {Promise} - Delete response
 */
export const deleteImage = async (filename) => {
  try {
    const response = await api.delete(`/storage/blogs/media/images/${filename}`);

    if (response.status === 'success') {
      return {
        success: true,
        message: response.message || 'Image deleted successfully',
        filename: response.filename || filename,
      };
    }

    return {
      success: false,
      error: response.message || response.error || 'Failed to delete image',
    };
  } catch (error) {
    console.error('Delete image error:', error);
    
    let errorMessage = 'Failed to delete image. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Delete a video file
 * @param {string} filename - Name of the video file to delete
 * @returns {Promise} - Delete response
 */
export const deleteVideo = async (filename) => {
  try {
    const response = await api.delete(`/storage/blogs/media/videos/${filename}`);

    if (response.status === 'success') {
      return {
        success: true,
        message: response.message || 'Video deleted successfully',
        filename: response.filename || filename,
      };
    }

    return {
      success: false,
      error: response.message || response.error || 'Failed to delete video',
    };
  } catch (error) {
    console.error('Delete video error:', error);
    
    let errorMessage = 'Failed to delete video. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

const mediaService = {
  uploadImage,
  uploadVideo,
  uploadImagesBulk,
  uploadVideosBulk,
  deleteImage,
  deleteVideo,
};
export default mediaService;

